import React from "react";
import { BadgePercent, Boxes, Download, Plus, Upload } from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "../components/common/PageHero";
import StatGrid from "../components/common/StatGrid";
import DataTableCard from "../components/common/DataTableCard";


export default function Products() {
  return (
    <div className="erp-page-grid">
      <PageHero
        badge="Product master"
        title="Sharper product management with clearer pricing controls."
        subtitle="Categories, product definitions, bulk updates and rate logic now feel like a modern software workspace instead of a decorative dashboard."
        actions={
          <>
            <motion.button className="erp-btn erp-btn-ghost" whileTap={{ scale: 0.97 }}>
              <Upload size={15} /> Import catalog
            </motion.button>
            <motion.button className="erp-btn erp-btn-primary" whileTap={{ scale: 0.97 }}>
              <Plus size={15} /> Add product
            </motion.button>
          </>
        }
      />

      <StatGrid
        items={[
          {
            title: "Active SKUs",
            value: "1,284",
            meta: "Across finished and raw catalog",
            trend: "+46",
            tone: "success",
            icon: Boxes,
          },
          {
            title: "Category groups",
            value: "18",
            meta: "Well segmented for operations",
            trend: "Stable",
            tone: "neutral",
            icon: BadgePercent,
          },
          {
            title: "Pending updates",
            value: "12",
            meta: "Awaiting pricing approval",
            trend: "Review",
            tone: "warning",
            icon: Download,
          },
          {
            title: "Bulk templates",
            value: "7",
            meta: "Ready for upload teams",
            trend: "Ready",
            tone: "success",
            icon: Upload,
          },
        ]}
      />

      <div className="erp-grid erp-grid-2">
        <section className="erp-card">
          <div className="erp-card-head">
            <div>
              <h3>Catalog structure</h3>
              <p>Better overview of the product tree and where volume is concentrated.</p>
            </div>
          </div>
          <div className="erp-list compact">
            {[
              ["Premium certified", "Highest value per piece and strongest brand pull."],
              ["Fast moving standard", "Most dependable conversion category for steady cash flow."],
              ["Raw intake", "Supports manufacturing continuity and lot planning."],
              ["Special design sets", "Lower volume but excellent margin potential."],
            ].map(([title, text]) => (
              <div key={title} className="erp-list-row">
                <div className="erp-dot" />
                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="erp-card">
          <div className="erp-card-head">
            <div>
              <h3>Rate matrix health</h3>
              <p>Simple visual confidence check across grade and size combinations.</p>
            </div>
          </div>
          <div className="erp-matrix-grid">
            {[
              ["Grade A", "Aligned", "success"],
              ["Grade B", "Needs review", "warning"],
              ["Grade C", "Aligned", "success"],
              ["Certified", "Premium", "neutral"],
            ].map(([title, label, tone]) => (
              <motion.div
                key={title}
                className="erp-soft-tile"
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.985 }}
              >
                <strong>{title}</strong>
                <span className={`erp-pill ${tone}`}>{label}</span>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      <DataTableCard
        title="Product master list"
        subtitle="Focused table with product family, grade band, pricing mode and last update state."
        columns={[
          { key: "name", label: "Product" },
          { key: "category", label: "Category" },
          { key: "grade", label: "Grade" },
          { key: "pricing", label: "Pricing" },
          { key: "stock", label: "Stock" },
          { key: "status", label: "State" },
        ]}
        rows={[
          {
            name: "Ruby Oval 3mm",
            category: "Premium certified",
            grade: "A",
            pricing: "Dynamic margin",
            stock: "8,200 pcs",
            status: <span className="erp-pill success">Active</span>,
          },
          {
            name: "Amethyst Square 4mm",
            category: "Fast moving standard",
            grade: "B",
            pricing: "Rate matrix",
            stock: "12.4 kg",
            status: <span className="erp-pill warning">Review</span>,
          },
          {
            name: "Emerald Pair 5x7",
            category: "Special design sets",
            grade: "A+",
            pricing: "Manual premium",
            stock: "620 pcs",
            status: <span className="erp-pill neutral">Controlled</span>,
          },
        ]}
      />
    </div>
  );
}
